import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type MedicationTherapeuticalGroup from "parog-web/models/medication-therapeutical-group";
import type Patient from "parog-web/models/patient";
import type {
  ATCGroup,
  ATCOrMedicationName
} from "parog-web/utils/medication-request/group-by-atc";
import medicationRequestGroupByAtc from "parog-web/utils/medication-request/group-by-atc";
import tWithDefault from "parog-web/utils/t-with-default";
import type MedicationRequest from "./prescriptions/medication-request";

//---- Automatically generated, don't update this part ----

export default class TreatmentPrescriptionChangedJournalEvent extends JournalEvent {
  static type = "treatment-prescription-changed-journal-event";

  @attr("medication-requests") declare prescriptions: MedicationRequest[];

  /**
   * An array of ATC codes that are ignored.
   */
  @attr({
    defaultValue() {
      return [];
    }
  })
  declare ignoredAtcCodes: string[];

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.treatment-prescription-changed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "treatmentPrescriptionChangedJournalEvents"
  })
  declare patient: Patient;

  @hasMany("medication-therapeutical-group", { async: false, inverse: null })
  declare recommendedTherapeuticalClasses: MedicationTherapeuticalGroup[];

  @hasMany("medication-therapeutical-group", { async: false, inverse: null })
  declare notRecommendedTherapeuticalClasses: MedicationTherapeuticalGroup[];

  get groupedByATC(): Map<ATCOrMedicationName, ATCGroup> {
    return medicationRequestGroupByAtc(this.prescriptions);
  }

  get atcCodes(): string[] {
    return Array.from(this.groupedByATC.keys());
  }

  get hasAntiAnemiaTreatment(): boolean {
    return this.atcCodes.some(atc => atc.startsWith("B03X"));
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "treatment-prescription-changed-journal-event": TreatmentPrescriptionChangedJournalEvent;
  }
}
